/* theme.js — kernel only. Constructors land in sub-project 2.
   Sections declare behaviour with data-tlx / data-tlx-opts; nothing binds to
   a hardcoded element id. */
(function () {
  'use strict';

  var registry = Object.create(null);
  var instances = new WeakMap();

  function parseOpts(el) {
    var raw = el.getAttribute('data-tlx-opts');
    if (!raw) return {};
    try { return JSON.parse(raw); }
    catch (e) { console.warn('[TLX] bad data-tlx-opts on', el, e); return {}; }
  }

  var TLX = {
    register: function (name, ctor) { registry[name] = ctor; },

    boot: function (root) {
      root = root || document;
      var els = root.querySelectorAll('[data-tlx]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (instances.has(el)) continue;
        var Ctor = registry[el.getAttribute('data-tlx')];
        if (!Ctor) continue;
        try { instances.set(el, new Ctor(el, parseOpts(el))); }
        catch (e) { console.error('[TLX] failed to boot', el, e); }
      }
    },

    destroy: function (root) {
      root = root || document;
      var els = root.querySelectorAll('[data-tlx]');
      for (var i = 0; i < els.length; i++) {
        var inst = instances.get(els[i]);
        if (inst && typeof inst.destroy === 'function') inst.destroy();
        instances.delete(els[i]);
      }
    },

    get: function (el) { return instances.get(el); }
  };

  window.TLX = TLX;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { TLX.boot(); });
  } else {
    TLX.boot();
  }

  // Theme editor lifecycle. Without these, sections break on every edit.
  document.addEventListener('shopify:section:load',   function (e) { TLX.boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { TLX.destroy(e.target); });
}());

/* Popup — general-purpose, merchant-configured popup/modal.
   Markup + behaviour options come from sections/popup.liquid via
   data-tlx="popup" / data-tlx-opts. Nothing here is hardcoded: every trigger,
   frequency rule and close affordance reads from the options object. */
(function () {
  'use strict';

  if (!window.TLX) return;

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function prefersReducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      return false;
    }
  }

  function storageGet(key) {
    try { return window.localStorage.getItem(key); }
    catch (e) { return null; }
  }

  function storageSet(key, value) {
    try { window.localStorage.setItem(key, value); }
    catch (e) { /* privacy mode, quota exceeded, disabled storage — never fatal */ }
  }

  function toNumber(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  // Returns a cleanup function that removes the listener it just attached.
  function on(target, type, handler, opts) {
    target.addEventListener(type, handler, opts);
    return function () { target.removeEventListener(type, handler, opts); };
  }

  function Popup(el, rawOpts) {
    this.el = el;
    this.opts = Popup.normalizeOpts(rawOpts || {});
    this.overlay = el.querySelector('[data-tlx-popup-overlay]');
    this.box = el.querySelector('.tlx-popup__box');
    this.closeBtn = el.querySelector('[data-tlx-popup-close]');
    this.position = el.getAttribute('data-position') || 'center';
    this.storageKey = 'tlx:popup:' + (this.opts.sectionId || '') + ':lastShown';

    this.isOpen = false;
    this._designMode = isDesignMode();
    this._prevFocus = null;
    this._triggerFired = false;
    this._cleanups = [];

    this._onKeydown = this._onKeydown.bind(this);
    this._onOverlayClick = this._onOverlayClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);

    this._bindChrome();
    this._initCountdowns();

    if (!this.opts.enabled && !this._designMode) return;

    if (this._designMode) {
      // A merchant editing the section needs to see it, regardless of
      // trigger or frequency — otherwise there is nothing to edit.
      this.open();
      return;
    }

    this._bindTrigger();
  }

  Popup.normalizeOpts = function (o) {
    return {
      sectionId: o.sectionId || '',
      trigger: o.trigger || 'delay',
      delaySeconds: toNumber(o.delaySeconds, 5),
      scrollPercent: toNumber(o.scrollPercent, 50),
      inactivitySeconds: toNumber(o.inactivitySeconds, 30),
      clickSelector: o.clickSelector || '',
      frequency: o.frequency || 'once',
      frequencyDays: toNumber(o.frequencyDays, 7),
      showClose: o.showClose !== false,
      closeOnOverlay: o.closeOnOverlay !== false,
      closeOnEsc: o.closeOnEsc !== false,
      enabled: o.enabled !== false
    };
  };

  // ---- chrome: close button, overlay click, escape + focus trap ----------

  Popup.prototype._bindChrome = function () {
    if (this.closeBtn) {
      this._cleanups.push(on(this.closeBtn, 'click', this._onCloseClick));
    }
    if (this.overlay) {
      this._cleanups.push(on(this.overlay, 'click', this._onOverlayClick));
    }
    this._cleanups.push(on(document, 'keydown', this._onKeydown));
  };

  Popup.prototype._onCloseClick = function () {
    this.close();
  };

  Popup.prototype._onOverlayClick = function () {
    if (this.opts.closeOnOverlay) this.close();
  };

  Popup.prototype._onKeydown = function (e) {
    if (!this.isOpen) return;
    if (e.key === 'Escape' || e.keyCode === 27) {
      if (this.opts.closeOnEsc) this.close();
      return;
    }
    if (e.key === 'Tab' || e.keyCode === 9) {
      this._trapFocus(e);
    }
  };

  Popup.prototype._trapFocus = function (e) {
    var focusables = this._focusableEls();
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  Popup.prototype._focusableEls = function () {
    if (!this.box) return [];
    var nodes = this.box.querySelectorAll(FOCUSABLE_SELECTOR);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.offsetParent !== null || n === document.activeElement) out.push(n);
    }
    return out;
  };

  // ---- triggers ------------------------------------------------------------

  Popup.prototype._bindTrigger = function () {
    var self = this;

    function fireOnce() {
      if (self._triggerFired) return;
      self._triggerFired = true;
      if (self.shouldShow()) self.open();
    }

    switch (this.opts.trigger) {
      case 'load':
        fireOnce();
        break;

      case 'delay': {
        var t = setTimeout(fireOnce, Math.max(0, this.opts.delaySeconds) * 1000);
        this._cleanups.push(function () { clearTimeout(t); });
        break;
      }

      case 'scroll': {
        var onScroll = function () {
          if (self._scrollPercent() >= self.opts.scrollPercent) fireOnce();
        };
        this._cleanups.push(on(window, 'scroll', onScroll, { passive: true }));
        break;
      }

      case 'exit': {
        // Exit intent: the pointer leaves through the top of the viewport.
        var onExit = function (e) {
          if (e.clientY <= 0 && !e.relatedTarget) fireOnce();
        };
        this._cleanups.push(on(document, 'mouseout', onExit));
        break;
      }

      case 'inactivity':
        this._bindInactivity(fireOnce);
        break;

      case 'click': {
        if (!this.opts.clickSelector) break;
        // A click trigger is deliberately not one-shot: the element stays a
        // live opener for as long as the popup is on the page, so repeated
        // clicks keep working (subject to the frequency rule below).
        var onClick = function (e) {
          var target = e.target && e.target.closest && e.target.closest(self.opts.clickSelector);
          if (!target) return;
          e.preventDefault();
          if (self.shouldShow()) self.open(target);
        };
        this._cleanups.push(on(document, 'click', onClick));
        break;
      }
    }
  };

  Popup.prototype._scrollPercent = function () {
    var doc = document.documentElement;
    var scrollTop = window.pageYOffset || doc.scrollTop || 0;
    var height = (doc.scrollHeight || document.body.scrollHeight) - doc.clientHeight;
    if (height <= 0) return 100;
    return (scrollTop / height) * 100;
  };

  Popup.prototype._bindInactivity = function (fire) {
    var self = this;
    var events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    var timer = null;
    var removers = [];

    function reset() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        fire();
        teardown();
      }, Math.max(1, self.opts.inactivitySeconds) * 1000);
    }

    function teardown() {
      if (timer) clearTimeout(timer);
      timer = null;
      for (var i = 0; i < removers.length; i++) removers[i]();
      removers.length = 0;
    }

    for (var i = 0; i < events.length; i++) {
      removers.push(on(document, events[i], reset, { passive: true }));
    }
    reset();

    this._cleanups.push(teardown);
  };

  // ---- frequency (localStorage, never fatal) --------------------------------

  Popup.prototype.shouldShow = function () {
    if (this._designMode) return true;
    if (this.opts.frequency === 'always') return true;

    var stored = storageGet(this.storageKey);
    if (!stored) return true;

    if (this.opts.frequency === 'once') return false;

    if (this.opts.frequency === 'days') {
      var last = parseInt(stored, 10);
      if (isNaN(last)) return true;
      var elapsedDays = (Date.now() - last) / 86400000;
      return elapsedDays >= this.opts.frequencyDays;
    }

    return true;
  };

  Popup.prototype._markShown = function () {
    storageSet(this.storageKey, String(Date.now()));
  };

  // ---- open / close ----------------------------------------------------------

  Popup.prototype.open = function (opener) {
    if (this.isOpen) return;
    this.isOpen = true;
    this._prevFocus = (opener && opener.nodeType === 1) ? opener : document.activeElement;

    var reduceMotion = prefersReducedMotion();
    this.el.classList.toggle('tlx-popup--no-motion', reduceMotion);

    this.el.hidden = false;
    // Force layout so the entrance transition actually runs when the
    // "is-open" class is added on the next line.
    void this.el.offsetWidth;
    this.el.classList.add('is-open');

    if (this.position === 'center') {
      document.documentElement.classList.add('tlx-popup-locked');
    }

    this._markShown();

    var self = this;
    var focusFirst = function () {
      var focusables = self._focusableEls();
      var target = focusables[0] || self.box;
      if (target && typeof target.focus === 'function') target.focus();
    };
    if (reduceMotion) focusFirst();
    else setTimeout(focusFirst, 260);
  };

  Popup.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.el.classList.remove('is-open');
    document.documentElement.classList.remove('tlx-popup-locked');

    var self = this;
    var hide = function () { self.el.hidden = true; };
    if (prefersReducedMotion()) hide();
    else setTimeout(hide, 260);

    if (this._prevFocus && typeof this._prevFocus.focus === 'function') {
      this._prevFocus.focus();
    }
    this._prevFocus = null;
  };

  // ---- countdown blocks --------------------------------------------------

  Popup.prototype._initCountdowns = function () {
    var els = this.el.querySelectorAll('[data-tlx-countdown]');
    for (var i = 0; i < els.length; i++) this._startCountdown(els[i]);
  };

  Popup.prototype._startCountdown = function (el) {
    var iso = el.getAttribute('datetime');
    var end = iso ? new Date(iso).getTime() : NaN;
    if (isNaN(end)) return;

    function pad(n) { return (n < 10 ? '0' : '') + n; }

    var timer = null;
    function render() {
      var diff = end - Date.now();
      if (diff <= 0) {
        el.textContent = '00d 00h 00m 00s';
        el.classList.add('is-ended');
        clearInterval(timer);
        return;
      }
      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff % 86400000) / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      el.textContent = pad(d) + 'd ' + pad(h) + 'h ' + pad(m) + 'm ' + pad(s) + 's';
    }

    render();
    timer = setInterval(render, 1000);
    this._cleanups.push(function () { clearInterval(timer); });
  };

  // ---- teardown ------------------------------------------------------------

  Popup.prototype.destroy = function () {
    for (var i = 0; i < this._cleanups.length; i++) {
      try { this._cleanups[i](); }
      catch (e) { /* a cleanup must never block the rest from running */ }
    }
    this._cleanups.length = 0;
    document.documentElement.classList.remove('tlx-popup-locked');
    this.isOpen = false;
  };

  window.TLX.register('popup', Popup);
}());

// -----------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  const nav = document.querySelector('#caseNav');

  if (!nav) return;

  nav.addEventListener('click', (event) => {
    const link = event.target.closest('a');

    if (!link || !nav.contains(link)) return;

    nav.querySelectorAll('a.is-active').forEach((activeLink) => {
      activeLink.classList.remove('is-active');
    });

    link.classList.add('is-active');
  });
});